import { useAppearance } from '../hooks/use-appearance';

import type { DensityChoice, ThemeChoice } from '../lib/appearance';

/**
 * Aparência — a escolha de tema e densidade do operador, dentro do menu do usuário.
 *
 * **Por que aqui e não numa tela de preferências.** O Eixo 3 do Briefing 1 V2
 * define tema e densidade como preferência PESSOAL («não afetam outros
 * operadores nem definem capabilities do tenant»), e o menu do usuário já é o
 * lugar do que é pessoal — a presença do turno e o sair. Uma tela dedicada
 * exigiria rota, item de menu lateral e uma navegação inteira para dois
 * cliques; P4 do Caderno (resistência ao over-engineering).
 *
 * **Sem capability gate, deliberadamente.** Escolher a própria cor de tela não
 * é feature de pacote: é acessibilidade e conforto de quem passa 8h aqui —
 * inclusive o atendente em turno noturno, que o próprio Briefing nomeia. A
 * gravação no servidor é que depende de `user-preferences-manage` (Solo+,
 * herdada por todo papel de tenant); quando ela falha, a escolha continua
 * valendo neste navegador e o operador é avisado — vide `use-appearance.ts`.
 *
 * Radiogroup nativo (`role="radio"` + `aria-checked`) porque a escolha é
 * exclusiva e precisa ser navegável por teclado e anunciável por leitor de
 * tela — requisito WCAG AA do Design System.
 */

const THEME_OPTIONS: readonly { value: ThemeChoice; label: string; hint: string }[] = [
  { value: 'light', label: 'Claro', hint: 'Sempre claro' },
  { value: 'dark', label: 'Escuro', hint: 'Sempre escuro' },
  { value: 'system', label: 'Sistema', hint: 'Acompanha o sistema operacional' },
];

const DENSITY_OPTIONS: readonly { value: DensityChoice; label: string; hint: string }[] = [
  { value: 'comfortable', label: 'Confortável', hint: 'Espaçamento padrão' },
  { value: 'compact', label: 'Compacto', hint: 'Mais linhas na tela' },
];

export function AppearanceMenuSection(): JSX.Element {
  const { theme, resolvedTheme, density, setTheme, setDensity } = useAppearance();

  return (
    <>
      <div className="topbar__menu-title">Aparência</div>

      <div className="topbar__seg" role="radiogroup" aria-label="Tema da interface">
        {THEME_OPTIONS.map((o) => (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={theme === o.value}
            title={o.hint}
            className="topbar__seg-btn"
            onClick={() => setTheme(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>

      {/* Sem esta linha, «Sistema» é um rótulo que não informa nada: o operador
          não sabe o que o SO decidiu por ele. */}
      {theme === 'system' && (
        <p className="topbar__menu-hint">
          Seguindo o sistema — agora em {resolvedTheme === 'dark' ? 'escuro' : 'claro'}.
        </p>
      )}

      <div className="topbar__menu-title">Densidade</div>

      <div className="topbar__seg" role="radiogroup" aria-label="Densidade da interface">
        {DENSITY_OPTIONS.map((o) => (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={density === o.value}
            title={o.hint}
            className="topbar__seg-btn"
            onClick={() => setDensity(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </>
  );
}
